import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldCheck } from "lucide-react";
import PaymentOptions, { PaymentBadge } from "@/components/PaymentOptions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PayPage() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/payments/order`, { params: { order_id: orderId, email } })
      .then((r) => setOrder(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Order not found"));
  }, [orderId, email]);

  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24" data-testid="pay-page">
      <p className="font-tech text-xs uppercase tracking-[0.18em] text-sky-700 mb-2 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Secure payment</p>
      <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">Complete your order</h1>
      <p className="mt-3 text-base text-slate-500">Order <span className="font-tech text-slate-900">{orderId}</span></p>
      {error && <div className="mt-8 border border-red-200 bg-red-50 text-red-700 text-sm p-4" data-testid="pay-page-error">{error}. <Link to="/track" className="underline">Look up your order</Link>.</div>}
      {!order && !error && <Loader2 className="h-6 w-6 animate-spin text-slate-400 mt-10" />}
      {order && (
        <div className="mt-8 grid md:grid-cols-[1fr_280px] gap-6">
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <PaymentBadge payment={order.payment} />
              <span className="text-xs text-slate-500">{order.customer_name}</span>
            </div>
            <PaymentOptions orderId={order.order_id} email={email} payment={order.payment} total={order.total}
              onUpdate={(p) => setOrder({ ...order, payment: p })} />
          </div>
          <aside className="border border-slate-200 bg-white p-5 h-fit" data-testid="pay-page-summary">
            <p className="font-tech text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-3">Order summary</p>
            {order.items.map((l, i) => (
              <div key={i} className="flex justify-between text-sm py-1.5 border-b border-slate-100">
                <span className="text-slate-700">{l.name} <span className="text-slate-400 text-xs">{l.specification} × {l.qty}</span></span>
                <span className="font-tech">${l.line_total.toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between text-xs text-slate-500 pt-3"><span>Subtotal</span><span className="font-tech">${order.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-xs text-slate-500 pt-1"><span>Insured lab freight</span><span className="font-tech">${order.shipping.toFixed(2)}</span></div>
            <div className="flex justify-between text-base font-semibold pt-3 mt-2 border-t border-slate-200"><span>Total</span><span className="font-tech" data-testid="pay-page-total">${order.total.toFixed(2)}</span></div>
          </aside>
        </div>
      )}
    </main>
  );
}
